"use client";

import { Box, VStack, Text, CloseButton } from "@chakra-ui/react";
import { Emoji } from "@/_components/ui/Emoji";
import { useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { GoogleButton } from "@/_components/ui/socials/GoogleButton";
import { LinkedinButton } from "@/_components/ui/socials/LinkedinButton";
import { useFingerprint } from "@/_hooks/useFingerprint";
import { useSsoSignIn } from "@/_hooks/useSsoSignIn";
import { toaster } from "@/_components/lib/ui/toaster";
import { NextLink } from "@/_components/ui/NextLink";

interface SignupDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const BENEFIT_KEYS = [
  "actionPlan",
  "atsPdf",
  "saveCompare",
  "trackScores",
] as const;

export const SignupDialog = ({ isOpen, onClose }: SignupDialogProps) => {
  const t = useTranslations("resume_benchmark");
  const locale = useLocale();
  const { visitorId } = useFingerprint();
  const { signInAsCandidate } = useSsoSignIn();

  const returnTo = useMemo(
    () => `/${locale}/resume-benchmark/home`, // temporarily returnTo we will have candidate home page soon
    [locale],
  );

  const benefits = useMemo(
    () => BENEFIT_KEYS.map((key) => t(`signup.benefits.${key}`)),
    [t],
  );

  if (!isOpen) return null;

  const handleSsoSignIn = (provider: "google" | "linkedin") => {
    if (!visitorId) {
      toaster.error({
        title: t("signup.toastWaitTitle"),
        description: t("signup.toastWaitDescription"),
      });
      return;
    }

    signInAsCandidate(provider, {
      fingerprintId: visitorId,
      returnTo,
    });
  };

  return (
    <Box
      position="fixed"
      inset={0}
      zIndex={1000}
      bg="blackAlpha.800"
      backdropFilter="blur(8px)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
      onClick={(e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Box
        bg="blackAlpha.500"
        rounded="3xl"
        p={8}
        maxW="460px"
        w="full"
        boxShadow="shadow-primary-xl"
        position="relative"
      >
        <CloseButton
          position="absolute"
          top={4}
          right={4}
          color="neutral.500"
          _hover={{ color: "white" }}
          onClick={onClose}
        />
        <VStack gap={6} align="stretch">
          <VStack gap={2} textAlign="center">
            {/* <Text fontSize="2xl">🔐</Text> */}
            <Emoji emoji="locked-with-key" size={12} />
            <Text fontSize="3xl" fontWeight="black" color="white">
              {t("signup.title")}
            </Text>
            <Text fontSize="sm" color="neutral.400">
              {t("signup.description")}
            </Text>
          </VStack>

          <VStack gap={2} alignItems="stretch" w="full">
            {benefits.map((b) => (
              <Text key={b} fontSize="sm" color="neutral.300">
                {b}
              </Text>
            ))}
          </VStack>

          <VStack gap={3} align="stretch">
            <GoogleButton onClick={() => handleSsoSignIn("google")}>
              {t("signup.continueGoogle")}
            </GoogleButton>
            <LinkedinButton onClick={() => handleSsoSignIn("linkedin")}>
              {t("signup.continueLinkedin")}
            </LinkedinButton>
          </VStack>

          {/* <Flex align="center" gap={3}>
            <Box flex={1} h="1px" bg="whiteAlpha.100" />
            <Text fontSize="xs" color="whiteAlpha.400">
              or
            </Text>
            <Box flex={1} h="1px" bg="whiteAlpha.100" />
          </Flex> */}

          <NextLink
            href="/help-center/policy"
            target="_blank"
            fontSize="xs"
            color="primary.400"
            textAlign="center"
          >
            {t("signup.terms")}
          </NextLink>
        </VStack>
      </Box>
    </Box>
  );
};
